import { TooltipMenuItem } from '@/components/Header/HeaderContents/HeaderSearch/HeaderMenu/TooltipMenuItem/TooltipMenuItem';
import { useAppConstantsContext } from '@/context/AppConstants';
import { DataCard, openReportInNewTab } from '@/reports/index';
import {
  build as buildDataCardBadge,
  DataCardBadgeTruthinessMapping,
} from '@/reports/shared/DataCard/DataCardBadge/DataCardBadge';
import {
  build as buildTag,
  DataCardTagTruthinessMapping,
} from '@/reports/shared/DataCard/DataCardTag/DataCardTag';
import { commonTypes } from '@/reports/types';
import { DataList, Stack } from '@/theme/index';
import { formatPhone, getDatesSeen, nameize } from '@/utils/index';
import { isNewInfoItem } from '@/utils/isNewInfoItem/isNewInfoItem';

interface PhoneCardProps {
  phone: commonTypes.Phone;
  thumbRating: () => JSX.Element;
  ownerName: string;
}

export function PhoneCard({ phone, thumbRating, ownerName }: PhoneCardProps) {
  const datesSeen = getDatesSeen(phone);
  const {
    links: { baseUrl },
    config: { higherConfidenceThreshold },
  } = useAppConstantsContext();

  const badge = () => {
    const dataConfidence = phone?.meta?.confidence || 0;
    const owner = nameize(ownerName ?? '');

    const badgeMapping: DataCardBadgeTruthinessMapping[] = [
      {
        name: 'bestMatch',
        truthiness: phone?.meta?.is_best || false,
        insert: 'Phone Number',
      },
      {
        name: 'higherConfidence',
        truthiness: dataConfidence >= higherConfidenceThreshold,
        insert: owner,
      },
    ];

    return buildDataCardBadge(badgeMapping);
  };

  const clickableStyles = {
    marginBottom: 0,
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    textDecoration: 'underline',
    '&:hover': {
      cursor: 'pointer',
    },
  };

  const tag = () => {
    const tagMapping: DataCardTagTruthinessMapping[] = [
      {
        name: 'newInfo',
        truthiness: isNewInfoItem({
          newInfo: phone?.new_info,
          monitoredChangeType: phone?.meta?.monitored_change_type,
          monitoredOn: phone?.meta?.monitored_on,
        }),
      },
    ];

    return buildTag(tagMapping);
  };

  const title = phone.formattedPhone || formatPhone(phone.number || '');

  return (
    <DataCard
      badge={badge()}
      tag={tag()}
      thumbRating={thumbRating}
      titleProps={{
        title: title,
        onClick: () =>
          openReportInNewTab(
            { phone: phone.number || '', searchType: 'phone' },
            baseUrl
          ),
        sx: clickableStyles,
      }}
      buttonProps={{
        label: 'View Phone Report',
        onClick: () =>
          openReportInNewTab(
            { phone: phone.number || '', searchType: 'phone' },
            baseUrl
          ),
      }}
      cardStyles={{ mb: 0 }}
      titleTag={
        <Stack ml={1}>
          <TooltipMenuItem hideText={true} text={title} />
        </Stack>
      }
    >
      <DataList
        items={[
          ['Phone Type', phone.phoneType || phone.type],
          ['Service Provider', phone.carrier],
          ['Dates seen', datesSeen],
        ]}
      />
    </DataCard>
  );
}
